/**
 * Expense display utilities
 */

// Re-exports from @resida/shared — use these for all new code
import { formatUnitAddress } from '@resida/shared';
export { formatCalendarDate as formatExpenseDateTz, todayInTimezone } from '@resida/shared';

/**
 * Convert cents to display dollars: 4599 -> "$45.99"
 */
export function formatAmount(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

/**
 * Parse dollar input to cents: "45.99" -> 4599
 */
export function parseDollarsToCents(dollarStr: string): number {
  const parsed = parseFloat(dollarStr);
  if (isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
}

/**
 * Format a YYYY-MM-DD date string for display
 * e.g. "2024-01-15" -> "Jan 15, 2024"
 *
 * @deprecated Use formatCalendarDate(dateStr, timezone) from @resida/shared instead.
 * This function uses browser-local time (T12:00:00 noon hack) which does not
 * account for the property's timezone.
 */
export function formatExpenseDate(dateStr: string): string {
  // Parse as local noon to avoid UTC midnight → US timezone date rollback
  const date = new Date(`${dateStr}T12:00:00`);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a property/unit location for display: "Property Name · Unit 3" or just
 * "Property Name" when there is no unit (or the unit is the property-level sentinel).
 *
 * Goes through the shared unit-number helpers even though the service already normalizes
 * the single-family sentinel to null before it reaches the client — intentional defense
 * in depth (AP-001). Do not replace this with a truthiness check.
 */
export function formatExpenseLocation(propertyName: string | null, unitNumber: string | null): string {
  if (!propertyName) return '—';
  return formatUnitAddress(propertyName, unitNumber, ' · ');
}

/**
 * Format file size in human-readable form
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Convert cents to dollar string for input display: 4599 -> "45.99"
 */
export function centsToDollarString(cents: number): string {
  return (cents / 100).toFixed(2);
}
